// Third Party
import * as React from 'react';
import moment from 'moment';
import 'react-dates/initialize';
import {
  DayPickerRangeController,
  DayPickerRangeControllerShape,
  FocusedInputShape,
  isInclusivelyAfterDay,
} from 'react-dates';

// Utils
import { routes } from '@routes';
import { guestDescription } from '@utils/guest-description';
import { extractFirstPhraseBeforeComma } from '@utils/extract-first-phrase-before-comma';

// UI
import {
  PlacesSelectAutocomplete,
  PlacesSelectAutocompleteFilters,
} from '@components/places-select-autocomplete';
import { ButtonIcon } from '@components/button-icon';
import { Icon, IconNames } from '@components/icon';
import { Input } from '@components/form/input';
import { Button } from '@components/button';
import { Dropdown } from '@components/dropdown';
import { Typography } from '@components/typography';
import { guestCapacity } from '@utils/constants/guest-capacity-thresholds';

interface IHandleDatesChange {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
}

type Props = {
  placeName?: string;
  placeId?: string;
  setCheckInDate?: (date: moment.Moment | null) => void;
  setCheckOutDate?: (date: moment.Moment | null) => void;
};

// Cast DayPickerRangeController to ComponentType to resolve React JSX type conflicts
// This tells TypeScript to treat it as a valid React component that can accept props
const DayPickerComponent =
  DayPickerRangeController as React.ComponentType<DayPickerRangeControllerShape>;

export const SearchForm: React.FC<Props> = ({
  placeName,
  placeId,
  setCheckInDate,
  setCheckOutDate,
}) => {
  const [place, setPlace] = React.useState<
    { id: string; name: string } | undefined
  >({ id: placeId ? placeId : '', name: placeName ? placeName : '' });
  const [placeError, setPlaceError] = React.useState(false);
  const [checkInDate, _setCheckInDate] = React.useState<moment.Moment | null>(
    null
  );
  const [checkOutDate, _setCheckOutDate] = React.useState<moment.Moment | null>(
    null
  );
  const dateRangePickerInputRef = React.useRef<HTMLDivElement>(null);
  const [dateRangePickerFocusedInput, setDateRangePickerFocusedInput] =
    React.useState<FocusedInputShape | null>(null);

  const [guestCountHumanReadableValue, setGuestCountHumanReadableValue] =
    React.useState('');
  const guestCountInputRef = React.useRef<HTMLDivElement>(null);
  const [guestCountFocusedInput, setGuestCountFocusedInput] =
    React.useState(false);

  const [adultCount, setAdultCount] = React.useState(1);
  const [childCount, setChildCount] = React.useState(0);
  const [infantCount, setInfantCount] = React.useState(0);
  const [petCount, setPetCount] = React.useState(0);

  React.useEffect(() => {
    const description = guestDescription(
      adultCount,
      childCount,
      infantCount,
      petCount
    );

    setGuestCountHumanReadableValue(description);
  }, [adultCount, childCount, infantCount, petCount]);

  const handleDatesChange = ({ startDate, endDate }: IHandleDatesChange) => {
    setCheckInDate && setCheckInDate(startDate);
    _setCheckInDate(startDate);
    setCheckOutDate && setCheckOutDate(endDate);
    _setCheckOutDate(endDate);
  };

  const handleValueChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    maxValue: guestCapacity
  ) => {
    e.preventDefault();
    const re = /^[0-9\b]+$/;
    if (e.target.value.startsWith('0')) {
      e.target.value = e.target.value.slice(1);
    }
    if (e.target.value === '' || re.test(e.target.value)) {
      const inputValue = Number(e.target.value);
      const value = inputValue > maxValue ? maxValue : inputValue;
      setValue(value);
    }
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!place) {
      setPlaceError(true);
      return;
    }

    window.location.assign(
      routes.searchShow({
        placeId: place.id,
        checkInDate: checkInDate?.format('YYYY-MM-DD'),
        checkOutDate: checkOutDate?.format('YYYY-MM-DD'),
        adultCount: adultCount > 0 ? adultCount : 1,
        childCount,
        infantCount,
        petCount,
      })
    );
  };

  return (
    <div className="rounded-3xl bg-primary-xlight p-4 lg:rounded-full">
      <form onSubmit={onSubmit}>
        <div className="grid grid-cols-6 gap-2 lg:gap-0">
          <div className="col-span-6 flex w-full whitespace-nowrap lg:col-span-2">
            <PlacesSelectAutocomplete
              initialValue={
                placeName && placeId
                  ? {
                      label: extractFirstPhraseBeforeComma(placeName),
                      value: placeId || '',
                    }
                  : null
              }
              placesFilter={PlacesSelectAutocompleteFilters.REGIONS}
              isInvalid={placeError}
              showLabel={false}
              name="place"
              placeholder="Where to?"
              noResultsLabel="No results found"
              data-testid={'@search/search-input-place'}
              leftContent={
                <Icon
                  name={IconNames.MAGNIFYING_GLASS}
                  solid={true}
                  className="text-primary-main"
                />
              }
              onChange={(_name, option) => {
                setPlaceError(false);
                if (option) {
                  setPlace({ name: option.label, id: option.value });
                }
              }}
            />
          </div>

          <div className="col-span-6 flex w-full whitespace-nowrap lg:col-span-4">
            <div className="w-full min-w-[150px] lg:ml-2">
              <Input
                label="Check-in"
                name="checkInDate"
                value={checkInDate?.format('MMM DD')}
                data-testid={'@search/search-input-date'}
                onClick={() => {
                  setDateRangePickerFocusedInput('startDate');
                }}
                leftContent={
                  <Icon
                    name={IconNames.CALENDAR_RANGE}
                    className="text-primary-main"
                  />
                }
                rightContent={
                  checkInDate ? (
                    <ButtonIcon
                      variant="text"
                      color="gray"
                      size="sm"
                      icon={IconNames.CIRCLE_X}
                      iconSize="lg"
                      ariaDescribedby="clear check in date search input"
                      onClick={() => {
                        setCheckInDate && setCheckInDate(null);
                        _setCheckInDate(null);
                        setDateRangePickerFocusedInput('startDate');
                      }}
                      className="pb-4 pe-0"
                    />
                  ) : (
                    <ButtonIcon
                      variant="text"
                      // @ts-ignore
                      color={'white'}
                      size="sm"
                      className="pe-0 ps-0"
                    />
                  )
                }
                autoComplete="off"
                hasCaret={false}
              />
              <div className="absolute mt-2">
                <Dropdown
                  dropdownWidth={'635px'}
                  reference={dateRangePickerInputRef.current}
                  isOpen={!!dateRangePickerFocusedInput}
                  onClickAway={() => {
                    setDateRangePickerFocusedInput(null);
                  }}
                  data-id="date-range-picker-dropdown"
                  className="absolute"
                >
                  <div className="px-2">
                    <DayPickerComponent
                      verticalBorderSpacing={0}
                      enableOutsideDays={false}
                      minDate={null}
                      noBorder={true}
                      minimumNights={1}
                      numberOfMonths={2}
                      initialVisibleMonth={null}
                      focusedInput={dateRangePickerFocusedInput}
                      onDatesChange={handleDatesChange}
                      onFocusChange={(
                        dateRangePickerFocusedInput: FocusedInputShape | null
                      ) => {
                        setDateRangePickerFocusedInput(
                          dateRangePickerFocusedInput
                        );
                      }}
                      startDate={checkInDate}
                      endDate={checkOutDate}
                      isOutsideRange={(day: moment.Moment) =>
                        !isInclusivelyAfterDay(day, moment())
                      }
                      hideKeyboardShortcutsPanel={true}
                    />
                  </div>
                  <div className="flex w-full justify-end pb-2 pr-2">
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => {
                        _setCheckInDate(null);
                        _setCheckOutDate(null);
                        setDateRangePickerFocusedInput('startDate');
                      }}
                    >
                      Clear
                    </Button>
                  </div>
                </Dropdown>
              </div>
            </div>
            <div className="ml-1 w-full min-w-[140px] lg:ml-2">
              <Input
                label="Check-out"
                name="checkOutDate"
                data-testid={'@search/check-out-date'}
                value={checkOutDate?.format('MMM DD')}
                onClick={() => {
                  if (!checkInDate) {
                    setDateRangePickerFocusedInput('startDate');
                  } else {
                    setDateRangePickerFocusedInput('endDate');
                  }
                }}
                leftContent={
                  <Icon
                    name={IconNames.CALENDAR_RANGE}
                    className="text-primary-main"
                  />
                }
                rightContent={
                  checkOutDate ? (
                    <ButtonIcon
                      variant="text"
                      color="gray"
                      size="sm"
                      icon={IconNames.CIRCLE_X}
                      iconSize="lg"
                      ariaDescribedby="clear check on date search input"
                      onClick={() => {
                        setCheckOutDate && setCheckOutDate(null);
                        _setCheckOutDate(null);
                        setDateRangePickerFocusedInput('endDate');
                      }}
                      className="pe-0 ps-0"
                    />
                  ) : (
                    <ButtonIcon
                      variant="text"
                      // @ts-ignore
                      color={'white'}
                      size="sm"
                      className="pe-0 ps-0"
                    />
                  )
                }
                autoComplete="off"
                hasCaret={false}
              />
            </div>
            <div className="ml-1 w-full min-w-[120px] lg:ml-2">
              <Input
                hasCaret={false}
                label="Who?"
                name="guests"
                value={guestCountHumanReadableValue}
                onClick={() => {
                  setGuestCountFocusedInput(true);
                }}
                leftContent={
                  <Icon name={IconNames.USER} className="text-primary-main" />
                }
                rightContent={
                  guestCountHumanReadableValue ? (
                    <ButtonIcon
                      variant="text"
                      color="gray"
                      size="sm"
                      icon={IconNames.CIRCLE_X}
                      iconSize="lg"
                      ariaDescribedby="clear check out date search input"
                      onClick={() => {
                        setAdultCount(0);
                        setChildCount(0);
                        setInfantCount(0);
                        setPetCount(0);
                      }}
                      className="pe-0 ps-0"
                    />
                  ) : (
                    <ButtonIcon
                      variant="text"
                      // @ts-ignore
                      color={'white'}
                      size="sm"
                      className="pe-0 ps-0"
                    />
                  )
                }
                autoComplete="off"
              />
              <div className="absolute right-0 mt-2 xl:right-auto">
                <Dropdown
                  dropdownWidth={'400px'}
                  reference={guestCountInputRef.current}
                  isOpen={guestCountFocusedInput}
                  onClickAway={() => {
                    setGuestCountFocusedInput(false);
                  }}
                  data-id="guest-count-picker-dropdown"
                  className="space-y-4 divide-y divide-gray-light overflow-auto p-4 text-left"
                >
                  <div className="flex justify-between">
                    <div className="flex flex-col">
                      <Typography variant="subtitle2" component="p">
                        Adults
                      </Typography>
                      <Typography variant="body2" component="p">
                        Ages 13 or above
                      </Typography>
                    </div>
                    <div className="flex justify-between space-x-2">
                      <ButtonIcon
                        icon={IconNames.MINUS}
                        ariaDescribedby="remove adult"
                        variant="text"
                        className="border-gray-main text-gray-main"
                        onClick={() =>
                          setAdultCount((val) => {
                            if (val > 0) {
                              return val - 1;
                            } else {
                              return val;
                            }
                          })
                        }
                      />
                      <input
                        type="number"
                        className="w-6 bg-white text-center"
                        value={adultCount}
                        data-testid={'@search-form/adult-count'}
                        onChange={(e) => {
                          handleValueChange(
                            e,
                            setAdultCount,
                            guestCapacity.ADULTS
                          );
                        }}
                      />
                      <ButtonIcon
                        icon={IconNames.PLUS}
                        ariaDescribedby="add adult"
                        variant="text"
                        className="border-gray-main text-gray-main"
                        onClick={() =>
                          setAdultCount((val) => {
                            return val >= guestCapacity.ADULTS
                              ? guestCapacity.ADULTS
                              : val + 1;
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="flex justify-between pt-4">
                    <div className="flex flex-col">
                      <Typography variant="subtitle2" component="p">
                        Children
                      </Typography>
                      <Typography variant="body2" component="p">
                        Ages 2-12
                      </Typography>
                    </div>
                    <div className="flex justify-between space-x-2">
                      <ButtonIcon
                        icon={IconNames.MINUS}
                        ariaDescribedby="remove child"
                        variant="text"
                        className="border-gray-main text-gray-main"
                        onClick={() =>
                          setChildCount((val) => {
                            if (val > 0) {
                              return val - 1;
                            } else {
                              return val;
                            }
                          })
                        }
                      />
                      <input
                        type="number"
                        className="w-6 bg-white text-center"
                        data-testid={'@search-form/child-count'}
                        value={childCount}
                        onChange={(e) => {
                          handleValueChange(
                            e,
                            setChildCount,
                            guestCapacity.CHILDREN
                          );
                        }}
                      />
                      <ButtonIcon
                        icon={IconNames.PLUS}
                        ariaDescribedby="add child"
                        variant="text"
                        className="border-gray-main text-gray-main"
                        onClick={() =>
                          setChildCount((val) => {
                            return val >= guestCapacity.CHILDREN
                              ? guestCapacity.CHILDREN
                              : val + 1;
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="flex justify-between pt-4">
                    <div className="flex flex-col">
                      <Typography variant="subtitle2" component="p">
                        Infants
                      </Typography>
                      <Typography variant="body2" component="p">
                        Under 2
                      </Typography>
                    </div>
                    <div className="flex justify-between space-x-2">
                      <ButtonIcon
                        icon={IconNames.MINUS}
                        ariaDescribedby="remove infant"
                        variant="text"
                        className="border-gray-main text-gray-main"
                        onClick={() =>
                          setInfantCount((val) => {
                            if (val > 0) {
                              return val - 1;
                            } else {
                              return val;
                            }
                          })
                        }
                      />
                      <input
                        type="number"
                        className="w-6 bg-white text-center"
                        value={infantCount}
                        data-testid={'@search-form/infant-count'}
                        onChange={(e) => {
                          handleValueChange(
                            e,
                            setInfantCount,
                            guestCapacity.INFANTS
                          );
                        }}
                      />
                      <ButtonIcon
                        icon={IconNames.PLUS}
                        ariaDescribedby="add infant"
                        variant="text"
                        className="border-gray-main text-gray-main"
                        onClick={() =>
                          setInfantCount((val) => {
                            return val >= guestCapacity.INFANTS
                              ? guestCapacity.INFANTS
                              : val + 1;
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="flex justify-between pt-4">
                    <div className="flex flex-col">
                      <Typography variant="subtitle2" component="p">
                        Pets
                      </Typography>
                      <Typography variant="body2" component="p">
                        Only properties that allow
                      </Typography>
                      <Typography variant="body2" component="p">
                        pets will be shown
                      </Typography>
                    </div>
                    <div className="flex justify-between space-x-2">
                      <ButtonIcon
                        icon={IconNames.MINUS}
                        ariaDescribedby="remove pet"
                        variant="text"
                        className="border-gray-main text-gray-main"
                        onClick={() =>
                          setPetCount((val) => {
                            if (val > 0) {
                              return val - 1;
                            } else {
                              return val;
                            }
                          })
                        }
                      />
                      <input
                        type="number"
                        className="w-6 bg-white text-center"
                        value={petCount}
                        data-testid={'@search-form/pet-count'}
                        onChange={(e) => {
                          handleValueChange(e, setPetCount, guestCapacity.PETS);
                        }}
                      />
                      <ButtonIcon
                        icon={IconNames.PLUS}
                        ariaDescribedby="add pet"
                        variant="text"
                        className="border-gray-main text-gray-main"
                        onClick={() =>
                          setPetCount((val) => {
                            return val >= guestCapacity.PETS
                              ? guestCapacity.PETS
                              : val + 1;
                          })
                        }
                      />
                    </div>
                  </div>
                </Dropdown>
              </div>
            </div>

            <div className="flex items-center justify-center md:hidden">
              <Button type="submit" className="ml-1 h-12">
                <Icon name={IconNames.MAGNIFYING_GLASS} />
              </Button>
            </div>
            <div className="hidden md:flex">
              <Button
                type="submit"
                color="primary"
                className="ml-1 text-base lg:ml-2"
              >
                Search
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
